import classNames from 'classnames';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import bowser from 'bowser';
import React from 'react';
import axios from 'axios';
import JSZip from 'jszip';

import VM from 'scratch-vm';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import CommunityButton from './community-button.jsx';
import ShareButton from './share-button.jsx';
import {ComingSoonTooltip} from '../coming-soon/coming-soon.jsx';
import Divider from '../divider/divider.jsx';
import SaveStatus from './save-status.jsx';
import ProjectWatcher from '../../containers/project-watcher.jsx';
import MenuBarMenu from './menu-bar-menu.jsx';
import {MenuItem, MenuSection} from '../menu/menu.jsx';
import ProjectTitleInput from './project-title-input.jsx';
import AuthorInfo from './author-info.jsx';
import AccountNav from '../../containers/account-nav.jsx';
import LoginDropdown from './login-dropdown.jsx';
import SB3Downloader from '../../containers/sb3-downloader.jsx';
import DeletionRestorer from '../../containers/deletion-restorer.jsx';
import TurboMode from '../../containers/turbo-mode.jsx';
import MenuBarHOC from '../../containers/menu-bar-hoc.jsx';
import SettingsMenu from './settings-menu.jsx';
import moduleIcon from './module-icon.png';
import fileIconNew from './file-icon.png';
import undoIcon from './undo-icon.png';
import downloadIcon from './download-icon.png';
import settingsIcon from './settings-icon.png';
import fullScreenIcon from '../stage-header/icon--fullscreen.svg';
import unFullScreenIcon from '../stage-header/icon--unfullscreen.svg';

import {openTipsLibrary} from '../../reducers/modals';
import {setPlayer} from '../../reducers/mode';
import {
    isTimeTravel220022BC,
    isTimeTravel1920,
    isTimeTravel1990,
    isTimeTravel2020,
    isTimeTravelNow,
    setTimeTravel
} from '../../reducers/time-travel';
import {
    autoUpdateProject,
    getIsUpdating,
    getIsShowingProject,
    manualUpdateProject,
    requestNewProject,
    remixProject,
    saveProjectAsCopy
} from '../../reducers/project-state';
import {
    openAboutMenu,
    closeAboutMenu,
    aboutMenuOpen,
    openAccountMenu,
    closeAccountMenu,
    toggleAccountMenu,
    accountMenuOpen,
    openFileMenu,
    closeFileMenu,
    toggleFileMenu,
    fileMenuOpen,
    moduleMenuOpen,
    openModuleMenu,
    closeModuleMenu,
    toggleModuleMenu,
    openEditMenu,
    closeEditMenu,
    toggleEditMenu,
    editMenuOpen,
    openLoginMenu,
    closeLoginMenu,
    toggleLoginMenu,
    loginMenuOpen,
    openModeMenu,
    closeModeMenu,
    toggleModeMenu,
    modeMenuOpen,
    settingsMenuOpen,
    openSettingsMenu,
    closeSettingsMenu,
    toggleSettingsMenu
} from '../../reducers/menus';

import {
      connectServoLive as connectServoLiveAction,
      disconnectServoLive as disconnectServoLiveAction
  } from '../../reducers/servo-live';

import {
    connectServoLive as connectServoLiveTransport,
    disconnectServoLive as disconnectServoLiveTransport,
    getServoLiveStatus,
    listServoLivePorts,
    addServoLiveDisconnectListener
} from '../../lib/servo-live-transport'

import collectMetadata from '../../lib/collect-metadata';
import downloadBlob from '../../lib/download-blob';
import {callAndroidInterfaceMethod, hasAndroidInterfaceMethod} from '../../lib/app-interface.js';
import {safeLocalStorageRemove} from '../../lib/web-storage.js';

import styles from './menu-bar.css';

import helpIcon from '../../lib/assets/icon--tutorials.svg';
import mystuffIcon from './icon--mystuff.png';
import profileIcon from './icon--profile.png';
import remixIcon from './icon--remix.svg';
import dropdownCaret from './dropdown-caret.svg';
import aboutIcon from './icon--about.svg';
import fileIcon from './icon--file.svg';
import editIcon from './icon--edit.svg';

import scratchLogo from './xrobo-logo-new.png';
import ninetiesLogo from './nineties_logo.svg';
import catLogo from './cat_logo.svg';
import prehistoricLogo from './prehistoric-logo.svg';
import oldtimeyLogo from './oldtimey-logo.svg';
import refreshIcon from './icon--refresh.png';

import sharedMessages from '../../lib/shared-messages';
import {setProjectTitle} from '../../reducers/project-title';
import {EXAMPLE_PROJECTS, resolveExampleProjectUrl} from '../../lib/example-projects';
import {EXTREME_EXAMPLE_CATEGORIES} from '../../lib/extreme-example-projects';
import modalStyle from '../code-sender/modalStyle.css';

import CodeSender from '../../containers/code-sender.jsx';  

const ariaMessages = defineMessages({
    tutorials: {
        id: 'gui.menuBar.tutorialsLibrary',
        defaultMessage: 'Tutorials',
        description: 'accessibility text for the tutorials button'
    }
});

const compatibilityMessages = defineMessages({
    extremeProjectOnly: {
        id: 'gui.projectLoader.extremeProjectOnly',
        defaultMessage: 'This project is for the eXtreme module. Please switch to eXtreme from the module menu above and try loading it again.',
        description: 'Error shown when an eXtreme project is opened in another module'
    },
    xlineProjectOnly: {
        id: 'gui.projectLoader.xlineProjectOnly',
        defaultMessage: 'This project is for the X-LINE module. Please switch to X-LINE from the module menu above and try loading it again.',
        description: 'Error shown when an X-LINE project is opened in another module'
    },
    compatibilityModalTitle: {
        id: 'gui.projectLoader.compatibilityModalTitle',
        defaultMessage: 'This file cannot be opened here.',
        description: 'Title shown in the custom modal when the project module is incompatible'
    },
    compatibilityModalConfirm: {
        id: 'gui.projectLoader.compatibilityModalConfirm',
        defaultMessage: 'OK',
        description: 'Confirm button label for incompatible project modal'
    }
});


const getLocalizedLabel = (locale, english, korean) => (locale === 'ko' ? korean : english);

const toByteArray = content => {
    if (content instanceof Uint8Array) {
        return Promise.resolve(Array.from(content));
    }
    if (content instanceof ArrayBuffer) {
        return Promise.resolve(Array.from(new Uint8Array(content)));
    }
    if (content instanceof Blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(Array.from(new Uint8Array(reader.result)));
            reader.onerror = reject;
            reader.readAsArrayBuffer(content);
        });
    }
    if (content && typeof content.arrayBuffer === 'function') {
        return content.arrayBuffer().then(arrayBuffer => Array.from(new Uint8Array(arrayBuffer)));
    }
    return Promise.reject(new Error('Unsupported project data type.'));
};

const MenuBarItemTooltip = ({
    children,
    className,
    enable,
    id,
    place = 'bottom'
}) => {
    if (enable) {
        return (
            <React.Fragment>
                {children}
            </React.Fragment>
        );
    }
    return (
        <ComingSoonTooltip
            className={classNames(styles.comingSoon, className)}
            place={place}
            tooltipClassName={styles.comingSoonTooltip}
            tooltipId={id}
        >
            {children}
        </ComingSoonTooltip>
    );
};


MenuBarItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.string,
    place: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
};

const MenuItemTooltip = ({id, isRtl, children, className}) => (
    <ComingSoonTooltip
        className={classNames(styles.comingSoon, className)}
        isRtl={isRtl}
        place={isRtl ? 'left' : 'right'}
        tooltipClassName={styles.comingSoonTooltip}
        tooltipId={id}
    >
        {children}
    </ComingSoonTooltip>
);

MenuItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
    isRtl: PropTypes.bool
};

const AboutButton = props => (
    <Button
        className={classNames(styles.menuBarItem, styles.hoverable)}
        iconClassName={styles.aboutIcon}
        iconSrc={aboutIcon}
        onClick={props.onClick}
    />
);

AboutButton.propTypes = {
    onClick: PropTypes.func.isRequired
};

class MenuBar extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            exampleMenuOpen: false,
            loadingExampleId: null,
            exampleCategoryId: null,
            isBrowserFullscreen: false,
            pendingModule: null,
            isModuleConfirmOpen: false,
            isNewProjectConfirmOpen: false,
            pendingExampleProject: null,
            isExampleProjectConfirmOpen: false,
            isServoPortModalOpen: false,
            servoPortList: [],
            selectedServoPortPath: null,
            isServoFeedbackOpen: false,
            servoFeedbackTitle: '',
            servoFeedbackDescription: '',
            compatibilityErrorMessage: null
        };
        bindAll(this, [
            'checkModuleCompatibility',
            'closeExampleMenu',
            'closeCompatibilityModal',
            'handleClickNew',
            'handleClickRemix',
            'handleClickSave',
            'handleClickSaveAsCopy',
            'handleClickExampleMenu',
            'handleSelectExampleCategory',
            'handleFullscreenChange',
            'handleToggleFullScreen',
            'handleClickSeeCommunity',
            'handleClickShare',
            'handleSetMode',
            'closeNewProjectConfirm',
            'confirmNewProject',
            'closeExampleProjectConfirm',
            'confirmExampleProjectLoad',
            'loadExampleProject',
            'startNewProject',
            'closeModuleConfirm',
            'confirmModuleTransition',
            'handleKeyPress',
            'handleLoadExampleProject',
            'handleToggleServoLive',
            'closeServoPortModal',
            'handleChooseServoLivePort',
            'handleRefreshServoPorts',
            'handleSelectServoLivePort',
            'handleServoLivePortReleased',
            'startElectronServoLivePolling',
            'stopElectronServoLivePolling',
            'pollElectronServoLiveStatus',
            'closeServoFeedback',
            'showServoFeedback',
            'handleRestoreOption',
            'getSaveToComputerHandler',
            'restoreOptionMessage'
        ]);
    }
    componentDidMount () {
        document.addEventListener('keydown', this.handleKeyPress);
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        this._hasRestoredAppAutosave = false;
        this.removeServoLiveDisconnectListener = addServoLiveDisconnectListener(this.handleServoLivePortReleased);
        this.previousOnConnectionStateUpdate = window.onConnectionStateUpdate;
        this.connectionStateUpdateHandler = state => {
            if (typeof this.previousOnConnectionStateUpdate === 'function') {
                this.previousOnConnectionStateUpdate(state);
            }

            if (state === 'Disconnected') {
                void this.handleServoLivePortReleased();
            }
        };

        const getProjectTitleFromFilename = fileName => {
            if (!fileName) return '';
            const matches = fileName.match(/^(.*)\.(xc|sb|sb2|sb3)$/i);
            if (!matches) return fileName.substring(0, 100);
            return matches[1].substring(0, 100);
        };

        const loadProjectFromBase64 = (base64Data, options = {}) => {
            const {showSuccessAlert = false, fileName = 'Project.xc'} = options;
            const binaryData = atob(base64Data);
            const arrayBuffer = new Uint8Array(binaryData.length);
            for (let i = 0; i < binaryData.length; i++) {
                arrayBuffer[i] = binaryData.charCodeAt(i);
            }

            return this.checkModuleCompatibility(arrayBuffer)
                .then(moduleCompatibilityError => {
                    if (moduleCompatibilityError) {
                        this.setState({
                            compatibilityErrorMessage: moduleCompatibilityError
                        });
                        return null;
                    }
                    return this.props.vm.loadProject(arrayBuffer)
                        .then(() => {
                            const projectTitle = getProjectTitleFromFilename(fileName);
                            if (projectTitle) {
                                this.props.onSetProjectTitle(projectTitle);
                            }
                            if (showSuccessAlert) {
                                alert(`프로젝트 "${fileName}"가 성공적으로 로드되었습니다.`);
                            }
                        });
                })
                .catch(error => {
                    console.error('프로젝트 로드 중 오류:', error);
                    if (showSuccessAlert) {
                        alert('프로젝트 로드 중 오류가 발생했습니다.');
                    }
                    throw error;
                });
        };

        //안드로이드에서 파일 불러올때
        window.onProjectLoad = (fileName, base64Data) => {
            loadProjectFromBase64(base64Data, {showSuccessAlert: true, fileName});
        };

        window.onAppFullscreenChanged = isFullscreen => {
            this.setState({
                isBrowserFullscreen: Boolean(isFullscreen)
            });
        };

        window.onConnectionStateUpdate = this.connectionStateUpdateHandler;
        this.startElectronServoLivePolling();
    }
    componentDidUpdate (prevProps) {
        if (prevProps.servoLiveEnabled !== this.props.servoLiveEnabled) {
            if (this.props.servoLiveEnabled) {
                this.startElectronServoLivePolling();
            } else {
                this.stopElectronServoLivePolling();
            }
        }
    }
    componentWillUnmount () {
        document.removeEventListener('keydown', this.handleKeyPress);
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        this.stopElectronServoLivePolling();
        delete window.onProjectLoad;
        delete window.onAppFullscreenChanged;
        if (window.onConnectionStateUpdate === this.connectionStateUpdateHandler) {
            delete window.onConnectionStateUpdate;
        } else {
            window.onConnectionStateUpdate = this.previousOnConnectionStateUpdate;
        }
        if (this.removeServoLiveDisconnectListener) {
            this.removeServoLiveDisconnectListener();
            this.removeServoLiveDisconnectListener = null;
        }
    }
    startElectronServoLivePolling () {
        if (!this.isElectron() || !this.props.servoLiveEnabled || this.electronServoLivePollTimer) {
            return;
        }
        this.electronServoLivePollTimer = window.setInterval(() => {
            void this.pollElectronServoLiveStatus();
        }, 1000);
    }
    stopElectronServoLivePolling () {
        if (!this.electronServoLivePollTimer) {
            return;
        }
        window.clearInterval(this.electronServoLivePollTimer);
        this.electronServoLivePollTimer = null;
    }
    async pollElectronServoLiveStatus () {
        if (!this.isElectron() || !this.props.servoLiveEnabled) {
            return;
        }

        try {
            const status = await getServoLiveStatus();
            if (!status?.connected) {
                await this.handleServoLivePortReleased();
            }
        } catch (error) {
            console.warn('Failed to poll Servo Live status:', error);
        }
    }
    startNewProject () {
        this.props.onRequestCloseFile();
        this.props.onClickNew(this.props.canSave && this.props.canCreateNew);

        // 해당 모듈 자동저장 삭제
        const module = new URLSearchParams(window.location.search).get('module') || 'default';
        const saveKey = `xrobo_autosave_${module}`;
        safeLocalStorageRemove(saveKey);
        callAndroidInterfaceMethod('clearAutosave', module);
        console.log('autosave reset');
        this.props.onRequestCloseModule();
    }
    handleClickNew () {
        // if the project is dirty, and user owns the project, we will autosave.
        // but if they are not logged in and can't save, user should consider
        // downloading or logging in first.
        // Note that if user is logged in and editing someone else's project,
        // they'll lose their work.
        if (this.props.shouldConfirmReplaceProject()) {
            this.setState({isNewProjectConfirmOpen: true});
            this.props.onRequestCloseFile();
            this.props.onRequestCloseModule();
            return;
        }
        this.startNewProject();
    }
    handleClickRemix () {
        this.props.onClickRemix();
        this.props.onRequestCloseFile();
        this.props.onRequestCloseModule();
    }
    handleClickSave () {
        this.props.onClickSave();
        this.props.onRequestCloseFile();
        this.props.onRequestCloseModule();
    }
    handleClickSaveAsCopy () {
        this.props.onClickSaveAsCopy();
        this.props.onRequestCloseFile();
        this.props.onRequestCloseModule();
    }
    closeExampleMenu () {
        this.setState({
            exampleMenuOpen: false,
            loadingExampleId: null,
            exampleCategoryId: null,
            pendingExampleProject: null,
            isExampleProjectConfirmOpen: false
        });
    }
    closeCompatibilityModal () {
        this.setState({compatibilityErrorMessage: null});
    }
    async checkModuleCompatibility (projectBuffer) {
        const currentModule = new URLSearchParams(window.location.search).get('module') || 'default';
        if (currentModule !== 'extreme' && currentModule !== 'xline') {
            return null;
        }

        let projectJSON = null;
        try {
            const zip = await JSZip.loadAsync(projectBuffer);
            const projectFile = zip.file('project.json');
            if (projectFile) {
                projectJSON = JSON.parse(await projectFile.async('string'));
            }
        } catch (error) {
            try {
                const rawText = new TextDecoder().decode(projectBuffer);
                projectJSON = JSON.parse(rawText);
            } catch (jsonError) {
                console.warn('Could not inspect project module compatibility', error || jsonError);
                return null;
            }
        }

        if (!projectJSON || !Array.isArray(projectJSON.targets)) {
            return null;
        }

        let hasExtremeBlocks = false;
        let hasXlineBlocks = false;
        for (const target of projectJSON.targets) {
            const blocks = target && target.blocks ? Object.values(target.blocks) : [];
            for (const block of blocks) {
                const opcode = block && block.opcode;
                if (typeof opcode !== 'string') continue;
                if (opcode.startsWith('xrobo_')) hasExtremeBlocks = true;
                if (opcode.startsWith('xline_')) hasXlineBlocks = true;
                if (hasExtremeBlocks && hasXlineBlocks) break;
            }
            if (hasExtremeBlocks && hasXlineBlocks) break;
        }

        if (currentModule === 'extreme' && hasXlineBlocks) {
            return this.props.intl.formatMessage(compatibilityMessages.xlineProjectOnly);
        }
        if (currentModule === 'xline' && hasExtremeBlocks) {
            return this.props.intl.formatMessage(compatibilityMessages.extremeProjectOnly);
        }
        return null;
    }
    handleClickExampleMenu (event) {
        const target = event && event.target;
        const targetElement = target && target.nodeType === Node.TEXT_NODE ?
            target.parentElement :
            target;

        if (targetElement && targetElement.closest('ul')) {
            console.log('[ExampleFiles] menu mouseup ignored because the click came from the dropdown list');
            return;
        }

        const nextOpen = !this.state.exampleMenuOpen;
        console.log('[ExampleFiles] trigger toggled', {
            currentOpen: this.state.exampleMenuOpen,
            nextOpen
        });
        this.closeAllMenus();
        this.setState({
            exampleMenuOpen: nextOpen,
            loadingExampleId: null,
            exampleCategoryId: null
        });
    }
    handleSelectExampleCategory (categoryId) {
        return event => {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            this.setState({
                exampleCategoryId: categoryId,
                loadingExampleId: null
            });
        };
    }
    getCurrentModule () {
        return new URLSearchParams(window.location.search).get('module') || 'default';
    }
    handleFullscreenChange () {
        this.setState({
            isBrowserFullscreen: Boolean(document.fullscreenElement)
        });
    }
    closeServoPortModal () {
        this.setState({
            isServoPortModalOpen: false,
            servoPortList: [],
            selectedServoPortPath: null
        });
    }
    closeServoFeedback () {
        this.setState({
            isServoFeedbackOpen: false,
            servoFeedbackTitle: '',
            servoFeedbackDescription: ''
        });
    }
    showServoFeedback (title, description = '') {
        this.setState({
            isServoFeedbackOpen: true,
            servoFeedbackTitle: title,
            servoFeedbackDescription: description
        });
    }
    handleChooseServoLivePort (portPath) {
      this.setState({
          selectedServoPortPath: portPath
      });
    }
    handleToggleFullScreen () {
        if (hasAndroidInterfaceMethod('setAppFullscreen')) {
            const nextFullscreen = !this.state.isBrowserFullscreen;
            callAndroidInterfaceMethod('setAppFullscreen', nextFullscreen);
            this.setState({
                isBrowserFullscreen: nextFullscreen
            });
            return;
        }
        if (document.fullscreenElement) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
            return;
        }
        if (document.documentElement && document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        }
    }
    async loadExampleProject (exampleProject) {
        if (!exampleProject) return;

        this.setState({
            loadingExampleId: exampleProject.id,
            pendingExampleProject: null,
            isExampleProjectConfirmOpen: false
        });

        try {
            const exampleUrl = resolveExampleProjectUrl(exampleProject.assetPath);
            console.log('[ExampleFiles] menu item clicked', {
                exampleId: exampleProject.id,
                title: exampleProject.title,
                exampleUrl
            });

            const response = await fetch(exampleUrl);
            const contentType = response.headers.get('content-type') || '';
            console.log('[ExampleFiles] fetch completed', {
                exampleId: exampleProject.id,
                ok: response.ok,
                status: response.status,
                url: response.url,
                contentType
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch ${exampleUrl}: ${response.status}`);
            }
            if (contentType.includes('text/html')) {
                throw new Error(`Received HTML instead of project file for ${exampleUrl}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            console.log('[ExampleFiles] arrayBuffer loaded', {
                exampleId: exampleProject.id,
                byteLength: arrayBuffer.byteLength
            });
            await this.props.vm.loadProject(arrayBuffer);
            console.log('[ExampleFiles] vm.loadProject resolved', {
                exampleId: exampleProject.id
            });
            this.props.onSetProjectTitle(exampleProject.title);
            this.closeExampleMenu();
        } catch (error) {
            console.error('예제 프로젝트 로드 중 오류:', error);
            alert(this.props.locale === 'ko' ? // eslint-disable-line no-alert
                '예제 파일을 불러오지 못했습니다.' :
                'Failed to load the example project.');
            this.setState({loadingExampleId: null});
        }
    }
    handleLoadExampleProject (exampleProject) {
        return event => {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            if (this.state.loadingExampleId) return;

            if (this.props.shouldConfirmReplaceProject()) {
                this.setState({
                    exampleMenuOpen: false,
                    loadingExampleId: null,
                    pendingExampleProject: exampleProject,
                    isExampleProjectConfirmOpen: true
                });
                return;
            }

            this.loadExampleProject(exampleProject);
        };
    }
    handleClickSeeCommunity (waitForUpdate) {
        if (this.props.shouldSaveBeforeTransition()) {
            this.props.autoUpdateProject(); // save before transitioning to project page
            waitForUpdate(true); // queue the transition to project page
        } else {
            waitForUpdate(false); // immediately transition to project page
        }
    }
    handleClickShare (waitForUpdate) {
        if (!this.props.isShared) {
            if (this.props.canShare) { // save before transitioning to project page
                this.props.onShare();
            }
            if (this.props.canSave) { // save before transitioning to project page
                this.props.autoUpdateProject();
                waitForUpdate(true); // queue the transition to project page
            } else {
                waitForUpdate(false); // immediately transition to project page
            }
        }
    }
    handleSetMode (mode) {
        return () => {
            // Turn on/off filters for modes.
            if (mode === '1920') {
                document.documentElement.style.filter = 'brightness(.9)contrast(.8)sepia(1.0)';
                document.documentElement.style.height = '100%';
            } else if (mode === '1990') {
                document.documentElement.style.filter = 'hue-rotate(40deg)';
                document.documentElement.style.height = '100%';
            } else {
                document.documentElement.style.filter = '';
                document.documentElement.style.height = '';
            }

            // Change logo for modes
            if (mode === '1990') {
                document.getElementById('logo_img').src = ninetiesLogo;
            } else if (mode === '2020') {
                document.getElementById('logo_img').src = catLogo;
            } else if (mode === '1920') {
                document.getElementById('logo_img').src = oldtimeyLogo;
            } else if (mode === '220022BC') {
                document.getElementById('logo_img').src = prehistoricLogo;
            } else {
                document.getElementById('logo_img').src = this.props.logo;
            }

            this.props.onSetTimeTravelMode(mode);
        };
    }
    handleRestoreOption (restoreFun) {
        return () => {
            restoreFun();
            this.props.onRequestCloseEdit();
        };
    }
    handleKeyPress (event) {
        const modifier = bowser.mac ? event.metaKey : event.ctrlKey;
        if (modifier && event.key === 's') {
            this.props.onClickSave();
            event.preventDefault();
        }
    }
    //xrobo
    onStartSelectingFileUpload_ () {
        if(this.getOS() === 'Android'){
            if (window.AndroidInterface && typeof window.AndroidInterface.openSavedProjectList === 'function') {
                window.AndroidInterface.openSavedProjectList();
                this.closeAllMenus();

            } else {
                console.error('AndroidInterface or openFileSelector function is not available');
            }
        }
        else {
            this.props.onStartSelectingFileUpload();
        }
    }
    isElectron() {
        // Renderer process
        if (typeof window !== 'undefined' && typeof window.process === 'object' && window.process.type === 'renderer') {
            return true;
        }

        // Main process
        if (typeof process !== 'undefined' && typeof process.versions === 'object' && !!process.versions.electron) {
            return true;
        }

        // Detect the user agent when the `nodeIntegration` option is set to true
        return typeof navigator === 'object' && typeof navigator.userAgent === 'string' && navigator.userAgent.indexOf('Electron') >= 0;


    }
    handleClickModule (_module) {
        if (_module === this.getCurrentModule()) {
            this.props.onRequestCloseModule();
            return;
        }
        this.setState({
            pendingModule: _module,
            isModuleConfirmOpen: true
        });
        this.props.onRequestCloseModule();
    }

    closeModuleConfirm () {
        this.setState({
            pendingModule: null,
            isModuleConfirmOpen: false
        });
    }

    closeNewProjectConfirm () {
        this.setState({isNewProjectConfirmOpen: false});
    }

    confirmNewProject () {
        this.closeNewProjectConfirm();
        this.startNewProject();
    }

    closeExampleProjectConfirm () {
        this.setState({
            pendingExampleProject: null,
            isExampleProjectConfirmOpen: false
        });
    }

    confirmExampleProjectLoad () {
        const {pendingExampleProject} = this.state;
        if (!pendingExampleProject) {
            this.closeExampleProjectConfirm();
            return;
        }
        this.loadExampleProject(pendingExampleProject);
    }

    confirmModuleTransition () {
        const _module = this.state.pendingModule;
        if (!_module) {
            this.closeModuleConfirm();
            return;
        }
        this.closeModuleConfirm();
        window.__xroboSkipBeforeUnloadPrompt = true;
        if (window.electron?.desktop?.switchModule) {
            window.electron.desktop.switchModule({
                module: _module,
                locale: this.props.locale
            });
            return;
        }
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('module', _module);
        nextUrl.searchParams.set('locale', this.props.locale);
        nextUrl.searchParams.delete('local');
        window.location.href = nextUrl.toString();
    }

    getSaveToComputerHandler (downloadProjectCallback) {
        return () => {
            if(this.getOS() === 'Android'){
                const projectData = this.props.vm.saveProjectSb3().then(content => {
                    const projectBlob = new Blob([content], { type: 'application/octet-stream' });
                    const reader = new FileReader();
                    const this_ = this;
                    reader.onload = function () {
                        // AndroidInterface를 통해 파일 저장 요청
                        if (window.AndroidInterface && typeof window.AndroidInterface.saveProject === 'function') {
                            const base64Data = reader.result.split(',')[1]; // base64 인코딩된 데이터
                            const defaultBaseName = (this_.props.projectTitle || 'Project').trim() || 'Project';
                            const sanitizedBaseName = defaultBaseName.replace(/[/\\?%*:|"<>]/g, '') || 'Project';
                            const fileName = `${sanitizedBaseName}.xc`;
                            window.AndroidInterface.saveProject(base64Data, fileName);
                            this_.closeAllMenus();
                        } else {
                            console.error('AndroidInterface or saveProject function is not available');
                        }
                    };
                    reader.readAsDataURL(projectBlob);
                }).catch(error => {
                    console.error('Error saving project:', error);
                });
            }
            else {
                const defaultBaseName = (this.props.projectTitle || 'Project').trim() || 'Project';
                const requestedName = window.electron?.desktop?.saveProject ?
                    defaultBaseName :
                    prompt( // eslint-disable-line no-alert
                        this.props.locale === 'ko' ?
                            '저장할 파일 이름을 입력하세요.' :
                            'Enter a file name to save.',
                        defaultBaseName
                    );
                if (requestedName === null) {
                    return;
                }

                const sanitizedBaseName = requestedName.trim()
                    .replace(/\.(xc|sb3)$/i, '')
                    .replace(/[/\\?%*:|"<>]/g, '') || defaultBaseName;

                this.props.onRequestCloseFile();
                this.props.vm.saveProjectSb3().then(content => {
                    const finishTelemetry = () => {
                        if (this.props.onProjectTelemetryEvent) {
                            const metadata = collectMetadata(this.props.vm, this.props.projectTitle, this.props.locale);
                            this.props.onProjectTelemetryEvent('projectDidSave', metadata);
                        }
                    };
                    if (window.electron?.desktop?.saveProject) {
                        toByteArray(content).then(bytes => {
                            window.electron.desktop.saveProject({
                                defaultFileName: `${sanitizedBaseName}.xc`,
                                data: bytes
                            }).then(result => {
                                if (result?.success) {
                                    finishTelemetry();
                                }
                            }).catch(error => {
                                console.error('Desktop save failed, falling back to browser download.', error);
                                downloadBlob(`${sanitizedBaseName}.xc`, content);
                                finishTelemetry();
                            });
                        }).catch(error => {
                            console.error('Project serialization for desktop save failed, falling back to browser download.', error);
                            downloadBlob(`${sanitizedBaseName}.xc`, content);
                            finishTelemetry();
                        });
                        return;
                    }
                    downloadBlob(`${sanitizedBaseName}.xc`, content);
                    finishTelemetry();
                });
            }

        };
    }
    getOS() {
        const userAgent = window.navigator.userAgent;
        const platform = window.navigator.platform;
        const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
        const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
        const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
        let os = null;

        if (macosPlatforms.indexOf(platform) !== -1) {
            os = 'Mac OS';
        } else if (iosPlatforms.indexOf(platform) !== -1) {
            os = 'iOS';
        } else if (windowsPlatforms.indexOf(platform) !== -1) {
            os = 'Windows';
        } else if (/Android/.test(userAgent)) {
            os = 'Android';
        } else if (!os && /Linux/.test(platform)) {
            os = 'Linux';
        }

        return os;
    }

    async handleToggleServoLive () {

      const isAndroidApp =
      typeof window !== 'undefined' &&
      typeof window.AndroidInterface !== 'undefined' &&
      typeof window.AndroidInterface.connectServoLive === 'function';

      if (this.props.servoLiveEnabled) {
          try {
              await disconnectServoLiveTransport();
              this.props.onDisconnectServoLive();
          } catch (error) {
              console.error('Failed to disconnect servo live port:', error);

              this.showServoFeedback(
                  this.props.locale === 'ko' ? '라이브 서보를 종료하지 못했습니다.' : 'Could not turn off Servo Live.',
                  this.props.locale === 'ko' ? '포트 연결 상태를 확인한 뒤 다시 시도해 주세요.' : 'Check the port connection and try again.'
              );
          }
          return;
      }

      if (this.isElectron()) {
          try {
              const ports = await listServoLivePorts();

              if (!ports.length) {
                  this.showServoFeedback(
                      this.props.locale === 'ko' ? '연결된 시리얼 포트가 없습니다.' : 'No serial ports found.',
                      this.props.locale === 'ko' ? '기기를 연결한 뒤 다시 시도해 주세요.' : 'Connect a device and try again.'
                  );
                  return;
              }

              this.setState({
                  isServoPortModalOpen: true,
                  servoPortList: ports,
                  selectedServoPortPath: ports.length === 1 ? ports[0].path : null
              });
          } catch (error) {
              console.error('Failed to load servo live ports:', error);

              this.showServoFeedback(
                  this.props.locale === 'ko' ? '라이브 서보 포트 연결에 실패했습니다.' : 'Failed to connect the Servo Live port.',
                  this.props.locale === 'ko' ? '포트 연결 상태를 확인한 뒤 다시 시도해 주세요.' : 'Check the port connection and try again.'
              );
          }
          return;
      }

      if (isAndroidApp) {
          try {
              const result = await connectServoLiveTransport();
              console.log('android servo result raw:', result);
              console.log('android servo result json:', JSON.stringify(result));
              console.log('android servo result success:', result?.success);
              console.log('android servo result connected:', result?.connected);

              if (result && (result.success || result.connected)) {
                  console.log('dispatching onConnectServoLive');
                  this.props.onConnectServoLive('android-usb');
              } else {
                  console.log('connect failed before dispatch');
                  throw new Error(result?.error || 'Failed to connect');
              }
          } catch (error) {
              console.error('Failed to connect servo live port:', error);
              this.showServoFeedback(
                  this.props.locale === 'ko' ? '서보 라이브 포트 연결에 실패했습니다.' : 'Failed to connect the Servo Live port.',
                  this.props.locale === 'ko' ? '포트 연결 상태를 확인한 뒤 다시 시도해 주세요.' : 'Check the port connection and try again.'
              );
          }
          return;
      }

      try {
          await connectServoLiveTransport();
          this.props.onConnectServoLive('web-serial');
      } catch (error) {
          console.error('Failed to connect servo live port:', error);
          this.showServoFeedback(
              this.props.locale === 'ko' ? '라이브 서보 포트 연결에 실패했습니다.' : 'Failed to connect the Servo Live port.',
              this.props.locale === 'ko' ? '포트 연결 상태를 확인한 뒤 다시 시도해 주세요.' : 'Check the port connection and try again.'
          );

      }
    }

    async handleServoLivePortReleased () {
        if (!this.props.servoLiveEnabled) {
            return;
        }

        try {
            await disconnectServoLiveTransport();
        } catch (error) {
            console.warn('Servo Live transport cleanup after port release failed:', error);
        } finally {
            this.props.onDisconnectServoLive();
        }
    }

  async handleRefreshServoPorts () {
      if (!this.isElectron()) return;

      try {
          const ports = await listServoLivePorts();
          const nextSelectedPortPath = ports.some(item => item.path === this.state.selectedServoPortPath) ?
              this.state.selectedServoPortPath :
              (ports.length === 1 ? ports[0].path : null);

          this.setState({
              servoPortList: ports,
              selectedServoPortPath: nextSelectedPortPath
          });
      } catch (error) {
          console.error('Failed to refresh servo live ports:', error);
          this.showServoFeedback(
              this.props.locale === 'ko' ? '시리얼 포트 목록을 새로고침하지 못했습니다.' : 'Failed to refresh the serial port list.',
              this.props.locale === 'ko' ? '잠시 후 다시 시도해 주세요.' : 'Please try again shortly.'
          );
      }
  }
    async handleSelectServoLivePort () {
        const {selectedServoPortPath} = this.state;
        if (!selectedServoPortPath) return;
        try {
            await connectServoLiveTransport(selectedServoPortPath)
            this.props.onConnectServoLive(selectedServoPortPath);
            this.closeServoPortModal();
        } catch (error) {
            console.error('Failed to connect servo live port:', error);

            this.showServoFeedback(
                this.props.locale === 'ko' ? '라이브 서보 포트 연결에 실패했습니다.' : 'Failed to connect the Servo Live port.',
                this.props.locale === 'ko' ? '포트 연결 상태를 확인한 뒤 다시 시도해 주세요.' : 'Check the port connection and try again.'
            );

        }
    }

    closeAllMenus() {
        const {
            onRequestCloseFile,
            onRequestCloseModule,
            onRequestCloseEdit,
            onRequestCloseLogin,
            onRequestCloseMode,
            onRequestCloseAbout,
            onRequestCloseAccount,
            onRequestCloseSettings
        } = this.props;

        // 모든 메뉴 닫기
        onRequestCloseFile();
        onRequestCloseModule();
        onRequestCloseEdit();
        onRequestCloseLogin();
        onRequestCloseMode();
        onRequestCloseAbout();
        onRequestCloseAccount();
        onRequestCloseSettings();
        this.closeExampleMenu();
    }


    restoreOptionMessage (deletedItem) {
        switch (deletedItem) {
        case 'Sprite':
            return (<FormattedMessage
                defaultMessage="Restore Sprite"
                description="Menu bar item for restoring the last deleted sprite."
                id="gui.menuBar.restoreSprite"
            />);
        case 'Sound':
            return (<FormattedMessage
                defaultMessage="Restore Sound"
                description="Menu bar item for restoring the last deleted sound."
                id="gui.menuBar.restoreSound"
            />);
        case 'Costume':
            return (<FormattedMessage
                defaultMessage="Restore Costume"
                description="Menu bar item for restoring the last deleted costume."
                id="gui.menuBar.restoreCostume"
            />);
        default: {
            return (<FormattedMessage
                defaultMessage="Restore"
                description="Menu bar item for restoring the last deleted item in its disabled state." /* eslint-disable-line max-len */
                id="gui.menuBar.restore"
            />);
        }
        }
    }
    buildAboutMenu (onClickAbout) {
        if (!onClickAbout) {
            // hide the button
            return null;
        }
        if (typeof onClickAbout === 'function') {
            // make a button which calls a function
            return <AboutButton onClick={onClickAbout} />;
        }
        // assume it's an array of objects
        // each item must have a 'title' FormattedMessage and a 'handleClick' function
        // generate a menu with items for each object in the array
        return (
            <div
                className={classNames(styles.menuBarItem, styles.hoverable, {
                    [styles.active]: this.props.aboutMenuOpen
                })}
                onMouseUp={this.props.onRequestOpenAbout}
            >
                <img
                    className={styles.aboutIcon}
                    src={aboutIcon}
                />
                <MenuBarMenu
                    className={classNames(styles.menuBarMenu)}
                    open={this.props.aboutMenuOpen}
                    place={this.props.isRtl ? 'right' : 'left'}
                    onRequestClose={this.props.onRequestCloseAbout}
                >
                    {
                        onClickAbout.map(itemProps => (
                            <MenuItem
                                key={itemProps.title}
                                isRtl={this.props.isRtl}
                                onClick={this.wrapAboutMenuCallback(itemProps.onClick)}
                            >
                                {itemProps.title}
                            </MenuItem>
                        ))
                    }
                </MenuBarMenu>
            </div>
        );
    }
    wrapAboutMenuCallback (callback) {
        return () => {
            callback();
            this.props.onRequestCloseAbout();
        };
    }
    render () {
        const saveNowMessage = (
            <FormattedMessage
                defaultMessage="Save now"
                description="Menu bar item for saving now"
                id="gui.menuBar.saveNow"
            />
        );
        const createCopyMessage = (
            <FormattedMessage
                defaultMessage="Save as a copy"
                description="Menu bar item for saving as a copy"
                id="gui.menuBar.saveAsCopy"
            />
        );
        const remixMessage = (
            <FormattedMessage
                defaultMessage="Remix"
                description="Menu bar item for remixing"
                id="gui.menuBar.remix"
            />
        );
        const newProjectMessage = (
            <FormattedMessage
                defaultMessage="New"
                description="Menu bar item for creating a new project"
                id="gui.menuBar.new"
            />
        );
        const remixButton = (
            <Button
                className={classNames(
                    styles.menuBarButton,
                    styles.remixButton
                )}
                iconClassName={styles.remixButtonIcon}
                iconSrc={remixIcon}
                onClick={this.handleClickRemix}
            >
                {remixMessage}
            </Button>
        );
        // Show the About button only if we have a handler for it (like in the desktop app)
        const aboutButton = this.buildAboutMenu(this.props.onClickAbout);
        const currentModule = this.getCurrentModule();
        const activeExtremeCategory = currentModule === 'extreme' ?
            EXTREME_EXAMPLE_CATEGORIES.find(category => category.id === this.state.exampleCategoryId) :
            null;
        const exampleMenuItems = currentModule === 'extreme' ? (
            activeExtremeCategory ? (
                <React.Fragment>
                    <MenuItem onClick={this.handleSelectExampleCategory(null)}>
                        {getLocalizedLabel(this.props.locale, '← Back', '← 목록으로')}
                    </MenuItem>
                    {activeExtremeCategory.items.map(exampleProject => (
                        <MenuItem
                            key={exampleProject.id}
                            onClick={this.handleLoadExampleProject(exampleProject)}
                        >
                            {this.state.loadingExampleId === exampleProject.id ?
                                `${exampleProject.title}...` :
                                exampleProject.title}
                        </MenuItem>
                    ))}
                </React.Fragment>
            ) : (
                EXTREME_EXAMPLE_CATEGORIES.map(category => (
                    <MenuItem
                        key={category.id}
                        onClick={this.handleSelectExampleCategory(category.id)}
                    >
                        {category.title}
                    </MenuItem>
                ))
            )
        ) : null;
        const isAndroidApp = this.getOS() === 'Android' &&
            typeof window.AndroidInterface !== 'undefined';
        return (
            <Box
                className={classNames(
                    this.props.className,
                    styles.menuBar
                )}
            >
                <div className={styles.mainMenu}>
                    <div className={styles.fileGroup}>
                        <div className={classNames(styles.menuBarItem, styles.logoTooltipWrapper)}>
                            <img
                                id="logo_img"
                                alt="Scratch"
                                className={classNames(styles.scratchLogo, {
                                    [styles.clickable]: typeof this.props.onClickLogo !== 'undefined' && !isAndroidApp
                                })}
                                draggable={false}
                                src={this.props.logo}
                                onClick={this.props.onClickLogo}
                            />
                            {isAndroidApp ? null : (
                                <span className={styles.logoTooltip}>Xrobo 홈페이지</span>
                            )}
                        </div>

                        {/* 업로드 버튼 */}
                        <div className={classNames(styles.menuBarItem, styles.hoverable)} style={{padding: '0 0.55rem'}}>
                            <CodeSender />
                        </div>
                        
                        {/*xrobo 메뉴 선택하는 곳*/}
                        <div className={classNames(styles.moduleGroup, styles.hideOnMobile)}>
                            <div
                                className={classNames(styles.menuBarItem, styles.hoverable, {
                                    [styles.active]: this.props.moduleMenuOpen
                                })}
                                onClick={this.props.onClickModule}
                            >
                                <img src={moduleIcon} style={{width: '1.4rem', height: '1.3rem', marginRight: '0.3rem'}} />
                                <span className={classNames(styles.menuLabel, styles.primaryLabel)}>
                                    {getLocalizedLabel(this.props.locale, 'Module', '모듈 선택')}
                                </span>
                                <MenuBarMenu
                                    className={classNames(styles.menuBarMenu)}
                                    open={this.props.moduleMenuOpen}
                                    place={this.props.isRtl ? 'left' : 'right'}
                                    onRequestClose={this.props.onRequestCloseModule}
                                >
                                    <MenuSection>
                                        <MenuItem
                                            isRtl={this.props.isRtl}
                                            onClick={() => this.handleClickModule('extreme')}
                                        >
                                            <FormattedMessage
                                                defaultMessage="eXtreme"
                                                id="gui.menuBar.extreme"
                                            />
                                        </MenuItem>
                                        <MenuItem
                                            isRtl={this.props.isRtl}
                                            onClick={() => this.handleClickModule('xline')}
                                        >
                                            <FormattedMessage
                                                defaultMessage="X-LINE"
                                                id="gui.menuBar.xline"
                                            />
                                        </MenuItem>
                                    </MenuSection>
                                </MenuBarMenu>
                            </div>
                        </div>
                        <div
                            className={classNames(styles.menuBarItem, styles.hoverable, styles.exampleFilesItem, {
                                [styles.active]: this.state.exampleMenuOpen
                            })}
                            onClick={this.handleClickExampleMenu}
                        >
                            <img src={downloadIcon} style={{width: '2.4rem', height: '2.4rem',marginBottom: '0.2rem'}} />
                            <span className={classNames(styles.menuLabel, styles.secondaryLabel)}>
                                {getLocalizedLabel(this.props.locale, 'Example Files', '예제 파일')}
                            </span>
                            <MenuBarMenu
                                className={classNames(styles.menuBarMenu)}
                                open={this.state.exampleMenuOpen}
                                place={this.props.isRtl ? 'left' : 'right'}
                                onRequestClose={this.closeExampleMenu}
                            >
                                <MenuSection>
                                    {exampleMenuItems}
                                </MenuSection>
                            </MenuBarMenu>
                        </div>
                        {(this.props.canManageFiles) && (
                            <div
                                className={classNames(styles.menuBarItem, styles.hoverable, {
                                    [styles.active]: this.props.fileMenuOpen
                                })}
                                onClick={this.props.onClickFile}
                            >
                                <img src={fileIconNew} style={{width: '1.4rem', height: '1.2rem',marginRight:'0.15rem'}} />
                                <span className={classNames(styles.collapsibleLabel, styles.menuLabel, styles.primaryLabel)}>
                                    <FormattedMessage
                                        defaultMessage="File"
                                        description="Text for file dropdown menu"
                                        id="gui.menuBar.file"
                                    />
                                </span>
                                {/* <img src={dropdownCaret} /> */}
                                <MenuBarMenu
                                    className={classNames(styles.menuBarMenu)}
                                    open={this.props.fileMenuOpen}
                                    place={this.props.isRtl ? 'left' : 'right'}
                                    onRequestClose={this.props.onRequestCloseFile}
                                >
                                    <MenuSection>
                                        <MenuItem
                                            isRtl={this.props.isRtl}
                                            onClick={this.handleClickNew}
                                        >
                                            {newProjectMessage}
                                        </MenuItem>
                                    </MenuSection>
                                    {(this.props.canSave || this.props.canCreateCopy || this.props.canRemix) && (
                                        <MenuSection>
                                            {this.props.canSave && (
                                                <MenuItem onClick={this.handleClickSave}>
                                                    {saveNowMessage}
                                                </MenuItem>
                                            )}
                                            {this.props.canCreateCopy && (
                                                <MenuItem onClick={this.handleClickSaveAsCopy}>
                                                    {createCopyMessage}
                                                </MenuItem>
                                            )}
                                            {this.props.canRemix && (
                                                <MenuItem onClick={this.handleClickRemix}>
                                                    {remixMessage}
                                                </MenuItem>
                                            )}
                                        </MenuSection>
                                    )}
                                    <MenuSection>
                                        <MenuItem
                                            onClick={()=>this.onStartSelectingFileUpload_()}
                                        >
                                            {this.props.intl.formatMessage(sharedMessages.loadFromComputerTitle)}
                                        </MenuItem>
                                        <SB3Downloader>{(className, downloadProjectCallback) => (
                                            <MenuItem
                                                className={className}
                                                onClick={this.getSaveToComputerHandler(downloadProjectCallback)}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Save to your computer"
                                                    description="Menu bar item for downloading a project to your computer" // eslint-disable-line max-len
                                                    id="gui.menuBar.downloadToComputer"
                                                />
                                            </MenuItem>
                                        )}</SB3Downloader>
                                    </MenuSection>
                                </MenuBarMenu>
                            </div>
                        )}
                        {/* <div
                            className={classNames(styles.menuBarItem, styles.hoverable, styles.hideOnTablet, {
                                [styles.active]: this.props.editMenuOpen
                            })}
                            onMouseUp={this.props.onClickEdit}
                        >
                            <img src={editIcon} />
                            <span className={styles.collapsibleLabel}>
                                <FormattedMessage
                                    defaultMessage="Edit"
                                    description="Text for edit dropdown menu"
                                    id="gui.menuBar.edit"
                                />
                            </span>
                            {/* <img src={dropdownCaret} /> */}
                            {/* <MenuBarMenu
                                className={classNames(styles.menuBarMenu)}
                                open={this.props.editMenuOpen}
                                place={this.props.isRtl ? 'left' : 'right'}
                                onRequestClose={this.props.onRequestCloseEdit}
                            >
                                <DeletionRestorer>{(handleRestore, {restorable, deletedItem}) => (
                                    <MenuItem
                                        className={classNames({[styles.disabled]: !restorable})}
                                        onClick={this.handleRestoreOption(handleRestore)}
                                    >
                                        {this.restoreOptionMessage(deletedItem)}
                                    </MenuItem>
                                )}</DeletionRestorer>
                                <MenuSection>
                                    <TurboMode>{(toggleTurboMode, {turboMode}) => (
                                        <MenuItem onClick={toggleTurboMode}>
                                            {turboMode ? (
                                                <FormattedMessage
                                                    defaultMessage="Turn off Turbo Mode"
                                                    description="Menu bar item for turning off turbo mode"
                                                    id="gui.menuBar.turboModeOff"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Turn on Turbo Mode"
                                                    description="Menu bar item for turning on turbo mode"
                                                    id="gui.menuBar.turboModeOn"
                                                />
                                            )}
                                        </MenuItem>
                                    )}</TurboMode>
                                </MenuSection>
                            </MenuBarMenu>

                        </div> */} 
                        {this.props.isTotallyNormal && (
                            <div
                                className={classNames(styles.menuBarItem, styles.hoverable, {
                                    [styles.active]: this.props.modeMenuOpen
                                })}
                                onMouseUp={this.props.onClickMode}
                            >
                                <div className={classNames(styles.editMenu)}>
                                    <FormattedMessage
                                        defaultMessage="Mode"
                                        description="Mode menu item in the menu bar"
                                        id="gui.menuBar.modeMenu"
                                    />
                                </div>
                                <MenuBarMenu
                                    className={classNames(styles.menuBarMenu)}
                                    open={this.props.modeMenuOpen}
                                    place={this.props.isRtl ? 'left' : 'right'}
                                    onRequestClose={this.props.onRequestCloseMode}
                                >
                                    <MenuSection>
                                        <MenuItem onClick={this.handleSetMode('NOW')}>
                                            <span className={classNames({[styles.inactive]: !this.props.modeNow})}>
                                                {'✓'}
                                            </span>
                                            {' '}
                                            <FormattedMessage
                                                defaultMessage="Normal mode"
                                                description="April fools: resets editor to not have any pranks"
                                                id="gui.menuBar.normalMode"
                                            />
                                        </MenuItem>
                                        <MenuItem onClick={this.handleSetMode('2020')}>
                                            <span className={classNames({[styles.inactive]: !this.props.mode2020})}>
                                                {'✓'}
                                            </span>
                                            {' '}
                                            <FormattedMessage
                                                defaultMessage="Caturday mode"
                                                description="April fools: Cat blocks mode"
                                                id="gui.menuBar.caturdayMode"
                                            />
                                        </MenuItem>
                                    </MenuSection>
                                </MenuBarMenu>
                            </div>
                        )}
                    </div>
                    
                    {/*1280px이하에서는 숨김 (css에 처리돼있음)*/}
                    {this.props.canEditTitle ? (
                        <div className={classNames(styles.menuBarItem, styles.growable, styles.hideOnMobile)}>
                            <MenuBarItemTooltip
                                enable
                                id="title-field"
                            >
                                <ProjectTitleInput
                                    className={classNames(styles.titleFieldGrowable)}
                                />
                            </MenuBarItemTooltip>
                        </div>
                    ) : ((this.props.authorUsername && this.props.authorUsername !== this.props.username) ? (
                        <AuthorInfo
                            className={styles.authorInfo}
                            imageUrl={this.props.authorThumbnailUrl}
                            projectTitle={this.props.projectTitle}
                            userId={this.props.authorId}
                            username={this.props.authorUsername}
                        />
                    ) : null)}

                    {/* 되돌리기 버튼 */}
                    <div
                        className={classNames(styles.menuBarItem, styles.hoverable)}
                        onClick={() => {
                        const workspace = Blockly.getMainWorkspace();
                        if (workspace) workspace.undo(false);
                    }}
                    >
                                        <img src={undoIcon} style={{width: '1.2rem', height: '1.2rem', marginRight: '0.3rem'}} />
                    <span className={classNames(styles.menuLabel, styles.secondaryLabel)}>
                        {getLocalizedLabel(this.props.locale, 'Undo', '되돌리기')}
                    </span>
                </div>

                   <div className={classNames(styles.menuBarItem)} style={{marginLeft: 'auto'}}>
                    </div>

                        <div
                            className={classNames(styles.menuBarItem, styles.hoverable, styles.fullScreenItem)}
                            onMouseUp={this.handleToggleFullScreen}
                        >
                        <img
                            src={this.state.isBrowserFullscreen ? unFullScreenIcon : fullScreenIcon}
                            style={{width: '1.32rem', height: '1.32rem', marginRight: '0.14rem'}}
                        />
                        <span className={classNames(styles.menuLabel, styles.secondaryLabel)}>
                            {this.state.isBrowserFullscreen ?
                                getLocalizedLabel(this.props.locale, 'Exit Full Screen', '전체화면 종료') :
                                getLocalizedLabel(this.props.locale, 'Full Screen', '전체화면')}
                        </span>
                    </div>

                    {(this.props.canChangeTheme || this.props.canChangeLanguage) && (<SettingsMenu
                            canChangeLanguage={this.props.canChangeLanguage}
                            canChangeTheme={this.props.canChangeTheme}
                            isRtl={this.props.isRtl}
                            locale={this.props.locale}
                            onRequestClose={this.props.onRequestCloseSettings}
                            onRequestOpen={this.props.onClickSettings}
                            settingsMenuOpen={this.props.settingsMenuOpen}
                            servoLiveEnabled={this.props.servoLiveEnabled}
                            onToggleServoLive={this.handleToggleServoLive}
                        />)}

                    <div className={classNames(styles.menuBarItem)} style={{paddingRight: '1rem'}}>
                        <span
                            style={{
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                                position: 'relative',
                                top: '-2px'
                            }}
                        >
                            {(() => {
                                const mod = new URLSearchParams(window.location.search).get('module') || '';
                                if (mod === 'extreme') return 'eXtreme';
                                if (mod === 'xline') return 'X-LINE'
                                return mod.replace(/^x/i, 'X');
                            })()}
                        </span>
                    </div>
                    </div>
                    

                {/* show the proper UI in the account menu, given whether the user is
                logged in, and whether a session is available to log in with */}
                {/*<div className={styles.accountInfoGroup}>*/}
                {/*    <div className={styles.menuBarItem}>*/}
                {/*        {this.props.canSave && (*/}
                {/*            <SaveStatus />*/}
                {/*        )}*/}
                {/*    </div>*/}
                {/*    {this.props.sessionExists ? (*/}
                {/*        this.props.username ? (*/}
                {/*            // ************ user is logged in *************/}
                {/*            <React.Fragment>*/}
                {/*                <a href="/mystuff/">*/}
                {/*                    <div*/}
                {/*                        className={classNames(*/}
                {/*                            styles.menuBarItem,*/}
                {/*                            styles.hoverable,*/}
                {/*                            styles.mystuffButton*/}
                {/*                        )}*/}
                {/*                    >*/}
                {/*                        <img*/}
                {/*                            className={styles.mystuffIcon}*/}
                {/*                            src={mystuffIcon}*/}
                {/*                        />*/}
                {/*                    </div>*/}
                {/*                </a>*/}
                {/*                <AccountNav*/}
                {/*                    className={classNames(*/}
                {/*                        styles.menuBarItem,*/}
                {/*                        styles.hoverable,*/}
                {/*                        {[styles.active]: this.props.accountMenuOpen}*/}
                {/*                    )}*/}
                {/*                    isOpen={this.props.accountMenuOpen}*/}
                {/*                    isRtl={this.props.isRtl}*/}
                {/*                    menuBarMenuClassName={classNames(styles.menuBarMenu)}*/}
                {/*                    onClick={this.props.onClickAccount}*/}
                {/*                    onClose={this.props.onRequestCloseAccount}*/}
                {/*                    onLogOut={this.props.onLogOut}*/}
                {/*                />*/}
                {/*            </React.Fragment>*/}
                {/*        ) : (*/}
                {/*            // ********* user not logged in, but a session exists*/}
                {/*            // ********* so they can choose to log in*/}
                {/*            <React.Fragment>*/}
                {/*                <div*/}
                {/*                    className={classNames(*/}
                {/*                        styles.menuBarItem,*/}
                {/*                        styles.hoverable*/}
                {/*                    )}*/}
                {/*                    key="join"*/}
                {/*                    onMouseUp={this.props.onOpenRegistration}*/}
                {/*                >*/}
                {/*                    <FormattedMessage*/}
                {/*                        defaultMessage="Join Scratch"*/}
                {/*                        description="Link for creating a Scratch account"*/}
                {/*                        id="gui.menuBar.joinScratch"*/}
                {/*                    />*/}
                {/*                </div>*/}
                {/*                <div*/}
                {/*                    className={classNames(*/}
                {/*                        styles.menuBarItem,*/}
                {/*                        styles.hoverable*/}
                {/*                    )}*/}
                {/*                    key="login"*/}
                {/*                    onMouseUp={this.props.onClickLogin}*/}
                {/*                >*/}
                {/*                    <FormattedMessage*/}
                {/*                        defaultMessage="Sign in"*/}
                {/*                        description="Link for signing in to your Scratch account"*/}
                {/*                        id="gui.menuBar.signIn"*/}
                {/*                    />*/}
                {/*                    <LoginDropdown*/}
                {/*                        className={classNames(styles.menuBarMenu)}*/}
                {/*                        isOpen={this.props.loginMenuOpen}*/}
                {/*                        isRtl={this.props.isRtl}*/}
                {/*                        renderLogin={this.props.renderLogin}*/}
                {/*                        onClose={this.props.onRequestCloseLogin}*/}
                {/*                    />*/}
                {/*                </div>*/}
                {/*            </React.Fragment>*/}
                {/*        )*/}
                {/*    ) : (*/}
                {/*        // ******** no login session is available, so don't show login stuff*/}
                {/*        <React.Fragment>*/}
                {/*            {this.props.showComingSoon ? (*/}
                {/*                <React.Fragment>*/}
                {/*                    <MenuBarItemTooltip id="mystuff">*/}
                {/*                        <div*/}
                {/*                            className={classNames(*/}
                {/*                                styles.menuBarItem,*/}
                {/*                                styles.hoverable,*/}
                {/*                                styles.mystuffButton*/}
                {/*                            )}*/}
                {/*                        >*/}
                {/*                            <img*/}
                {/*                                className={styles.mystuffIcon}*/}
                {/*                                src={mystuffIcon}*/}
                {/*                            />*/}
                {/*                        </div>*/}
                {/*                    </MenuBarItemTooltip>*/}
                {/*                    <MenuBarItemTooltip*/}
                {/*                        id="account-nav"*/}
                {/*                        place={this.props.isRtl ? 'right' : 'left'}*/}
                {/*                    >*/}
                {/*                        <div*/}
                {/*                            className={classNames(*/}
                {/*                                styles.menuBarItem,*/}
                {/*                                styles.hoverable,*/}
                {/*                                styles.accountNavMenu*/}
                {/*                            )}*/}
                {/*                        >*/}
                {/*                            <img*/}
                {/*                                className={styles.profileIcon}*/}
                {/*                                src={profileIcon}*/}
                {/*                            />*/}
                {/*                            <span>*/}
                {/*                                {'scratch-cat'}*/}
                {/*                            </span>*/}
                {/*                            <img*/}
                {/*                                className={styles.dropdownCaretIcon}*/}
                {/*                                src={dropdownCaret}*/}
                {/*                            />*/}
                {/*                        </div>*/}
                {/*                    </MenuBarItemTooltip>*/}
                {/*                </React.Fragment>*/}
                {/*            ) : []}*/}
                {/*        </React.Fragment>*/}
                {/*    )}*/}
                {/*</div>*/}

            
            {aboutButton}
            {this.state.isModuleConfirmOpen ? (
                <div className={styles.moduleConfirmOverlay}>
                    <div className={styles.moduleConfirmCard}>
                        <div className={styles.moduleConfirmTitle}>
                            {getLocalizedLabel(this.props.locale, 'Move to another module?', '다른 모듈로 이동하시겠습니까?')}
                        </div>
                        <div className={styles.moduleConfirmDescription}>
                            {getLocalizedLabel(this.props.locale, 'Your current work will be saved automatically.', '현재 작업 내용은 자동으로 저장됩니다.')}
                        </div>
                        <div className={styles.moduleConfirmActions}>
                            <button
                                className={styles.moduleConfirmButtonSecondary}
                                onClick={this.closeModuleConfirm}
                                type="button"
                            >
                                {getLocalizedLabel(this.props.locale, 'Cancel', '취소')}
                            </button>
                            <button
                                className={styles.moduleConfirmButtonPrimary}
                                onClick={this.confirmModuleTransition}
                                type="button"
                            >
                                {getLocalizedLabel(this.props.locale, 'Move', '이동')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            {this.state.isNewProjectConfirmOpen ? (
                <div className={styles.moduleConfirmOverlay}>
                    <div className={styles.moduleConfirmCard}>
                        <div className={styles.moduleConfirmTitle}>
                            {getLocalizedLabel(this.props.locale, 'Create a new project?', '새 프로젝트를 만드시겠습니까?')}
                        </div>
                        <div className={styles.moduleConfirmDescription}>
                            {getLocalizedLabel(this.props.locale, 'Your current content may be removed.', '현재 작업 내용이 사라질 수 있습니다.')}
                        </div>
                        <div className={styles.moduleConfirmActions}>
                            <button
                                className={styles.moduleConfirmButtonSecondary}
                                onClick={this.closeNewProjectConfirm}
                                type="button"
                            >
                                {getLocalizedLabel(this.props.locale, 'Cancel', '취소')}
                            </button>
                            <button
                                className={styles.moduleConfirmButtonPrimary}
                                onClick={this.confirmNewProject}
                                type="button"
                            >
                                {getLocalizedLabel(this.props.locale, 'Create New', '새로 만들기')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            {this.state.isExampleProjectConfirmOpen ? (
                <div className={styles.moduleConfirmOverlay}>
                    <div className={styles.moduleConfirmCard}>
                        <div className={styles.moduleConfirmTitle}>
                            {getLocalizedLabel(this.props.locale, 'Load this example project?', '예제 파일을 불러오시겠습니까?')}
                        </div>
                        <div className={styles.moduleConfirmDescription}>
                            {getLocalizedLabel(this.props.locale, 'Your current work may be removed.', '현재 작업 내용이 사라질 수 있습니다.')}
                        </div>
                        <div className={styles.moduleConfirmActions}>
                            <button
                                className={styles.moduleConfirmButtonSecondary}
                                onClick={this.closeExampleProjectConfirm}
                                type="button"
                            >
                                {getLocalizedLabel(this.props.locale, 'Cancel', '취소')}
                            </button>
                            <button
                                className={styles.moduleConfirmButtonPrimary}
                                onClick={this.confirmExampleProjectLoad}
                                type="button"
                            >
                                {getLocalizedLabel(this.props.locale, 'Load', '불러오기')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            {this.state.compatibilityErrorMessage ? (
                <div className={styles.moduleConfirmOverlay}>
                    <div className={styles.moduleConfirmCard}>
                        <div className={styles.moduleConfirmTitle}>
                            {this.props.intl.formatMessage(compatibilityMessages.compatibilityModalTitle)}
                        </div>
                        <div className={styles.moduleConfirmDescription}>
                            {this.state.compatibilityErrorMessage}
                        </div>
                        <div className={styles.moduleConfirmActions}>
                            <button
                                className={styles.moduleConfirmButtonPrimary}
                                onClick={this.closeCompatibilityModal}
                                type="button"
                            >
                                {this.props.intl.formatMessage(compatibilityMessages.compatibilityModalConfirm)}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            {this.state.isServoPortModalOpen ? (
                <div className={modalStyle.overlay}>
                    <div className={modalStyle.container}>
                        <div className={modalStyle.header}>
                            <div className={modalStyle.headerAccent} />
                            <div className={modalStyle.headerText}>
                                {getLocalizedLabel(this.props.locale, 'Select Live Servo Port', '라이브 서보 포트 선택')}
                            </div>
                            <button
                                className={styles.servoPortRefreshButton}
                                onClick={this.handleRefreshServoPorts}
                                type="button"
                            >
                                <img
                                    alt={getLocalizedLabel(this.props.locale, 'Refresh', '새로고침')}
                                    className={styles.servoPortRefreshIcon}
                                    src={refreshIcon}
                                />
                                <span className={styles.servoPortRefreshTooltip}>
                                    {getLocalizedLabel(this.props.locale, 'Refresh port list', '포트 목록 새로고침')}
                                </span>
                            </button>
                        </div>
                        <div className={modalStyle.content}>
                            <div className={modalStyle.contentContent}>
                                <div className={modalStyle.statusSummary}>
                                    <div className={modalStyle.statusBadge}>
                                        {getLocalizedLabel(this.props.locale, 'Port Selection', '포트 선택')}
                                    </div>
                                    <div className={modalStyle.statusTitle}>
                                        {getLocalizedLabel(this.props.locale, 'Choose a port for Live Servo', '라이브 서보에 사용할 포트를 선택해 주세요')}
                                    </div>
                                </div>
                                <div className={modalStyle.serialWrapper}>
                                    {this.state.servoPortList.map((item, index) => (
                                        <button
                                            key={`${item.path || 'servo-port'}-${index}`}
                                            className={classNames(styles.servoPortOption, {
                                                [styles.servoPortOptionSelected]:
                                                    this.state.selectedServoPortPath === item.path
                                            })}
                                            onClick={() => this.handleChooseServoLivePort(item.path)}
                                            type="button"
                                        >
                                            <strong>{item.path}</strong>
                                            <span>{item.manufacturer || 'Unknown Manufacturer'}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className={styles.servoPortModalActions}>
                                    <button
                                        className={styles.moduleConfirmButtonPrimary}
                                        disabled={!this.state.selectedServoPortPath}
                                        onClick={this.handleSelectServoLivePort}
                                        type="button"
                                    >
                                        {getLocalizedLabel(this.props.locale, 'Connect', '연결')}
                                    </button>
                                    <button
                                        className={styles.moduleConfirmButtonSecondary}
                                        onClick={this.closeServoPortModal}
                                        type="button"
                                    >
                                        {getLocalizedLabel(this.props.locale, 'Cancel', '취소')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
            {this.state.isServoFeedbackOpen ? (
                <div className={styles.moduleConfirmOverlay}>
                    <div className={styles.moduleConfirmCard}>
                        <div className={styles.moduleConfirmTitle}>
                            {this.state.servoFeedbackTitle}
                        </div>
                        <div className={styles.moduleConfirmDescription}>
                            {this.state.servoFeedbackDescription}
                        </div>
                        <div className={styles.moduleConfirmActions}>
                            <button
                                className={styles.moduleConfirmButtonPrimary}
                                onClick={this.closeServoFeedback}
                                type="button"
                            >
                                {getLocalizedLabel(this.props.locale, 'OK', '확인')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            </Box>

            
        );
    }
}

MenuBar.propTypes = {
    aboutMenuOpen: PropTypes.bool,
    accountMenuOpen: PropTypes.bool,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    autoUpdateProject: PropTypes.func,
    canChangeLanguage: PropTypes.bool,
    canChangeTheme: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    className: PropTypes.string,
    confirmReadyToReplaceProject: PropTypes.func,
    currentLocale: PropTypes.string.isRequired,
    editMenuOpen: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    fileMenuOpen: PropTypes.bool,
    moduleMenuOpen: PropTypes.bool,
    intl: intlShape,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    isUpdating: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    loginMenuOpen: PropTypes.bool,
    logo: PropTypes.string,
    mode1920: PropTypes.bool,
    mode1990: PropTypes.bool,
    mode2020: PropTypes.bool,
    mode220022BC: PropTypes.bool,
    modeMenuOpen: PropTypes.bool,
    modeNow: PropTypes.bool,
    onClickAbout: PropTypes.oneOfType([
        PropTypes.func, // button mode: call this callback when the About button is clicked
        PropTypes.arrayOf( // menu mode: list of items in the About menu
            PropTypes.shape({
                title: PropTypes.string, // text for the menu item
                onClick: PropTypes.func // call this callback when the menu item is clicked
            })
        )
    ]),
    onClickAccount: PropTypes.func,
    onClickEdit: PropTypes.func,
    onClickFile: PropTypes.func,
    onClickModule: PropTypes.func,
    onClickLogin: PropTypes.func,
    onClickLogo: PropTypes.func,
    onClickMode: PropTypes.func,
    onClickNew: PropTypes.func,
    onClickRemix: PropTypes.func,
    onClickSave: PropTypes.func,
    onClickSaveAsCopy: PropTypes.func,
    onClickSettings: PropTypes.func,
    onLogOut: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onOpenTipLibrary: PropTypes.func,
    onProjectTelemetryEvent: PropTypes.func,
    onRequestCloseAbout: PropTypes.func,
    onRequestCloseAccount: PropTypes.func,
    onRequestCloseEdit: PropTypes.func,
    onRequestCloseFile: PropTypes.func,
    onRequestCloseModule: PropTypes.func,
    onRequestCloseLogin: PropTypes.func,
    onRequestCloseMode: PropTypes.func,
    onRequestCloseSettings: PropTypes.func,
    onRequestOpenAbout: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onSetTimeTravelMode: PropTypes.func,
    onSetProjectTitle: PropTypes.func,
    onShare: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    projectTitle: PropTypes.string,
    renderLogin: PropTypes.func,
    sessionExists: PropTypes.bool,
    servoLiveEnabled: PropTypes.bool,
    settingsMenuOpen: PropTypes.bool,
    shouldConfirmReplaceProject: PropTypes.func,
    shouldSaveBeforeTransition: PropTypes.func,
    showComingSoon: PropTypes.bool,
    onConnectServoLive: PropTypes.func,
    onDisconnectServoLive: PropTypes.func,
    username: PropTypes.string,
    userOwnsProject: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};

MenuBar.defaultProps = {
    logo: scratchLogo,
    onShare: () => {}
};

const mapStateToProps = (state, ownProps) => {
    const loadingState = state.scratchGui.projectState.loadingState;
    const user = state.session && state.session.session && state.session.session.user;
    return {
        aboutMenuOpen: aboutMenuOpen(state),
        accountMenuOpen: accountMenuOpen(state),
        currentLocale: state.locales.locale,
        fileMenuOpen: fileMenuOpen(state),
        moduleMenuOpen: moduleMenuOpen(state),
        editMenuOpen: editMenuOpen(state),
        isRtl: state.locales.isRtl,
        isUpdating: getIsUpdating(loadingState),
        isShowingProject: getIsShowingProject(loadingState),
        locale: state.locales.locale,
        loginMenuOpen: loginMenuOpen(state),
        modeMenuOpen: modeMenuOpen(state),
        projectTitle: state.scratchGui.projectTitle,
        sessionExists: state.session && typeof state.session.session !== 'undefined',
        servoLiveEnabled: state.scratchGui.servoLive.enabled,
        settingsMenuOpen: settingsMenuOpen(state),
        username: user ? user.username : null,
        userOwnsProject: ownProps.authorUsername && user &&
            (ownProps.authorUsername === user.username),
        vm: state.scratchGui.vm,
        mode220022BC: isTimeTravel220022BC(state),
        mode1920: isTimeTravel1920(state),
        mode1990: isTimeTravel1990(state),
        mode2020: isTimeTravel2020(state),
        modeNow: isTimeTravelNow(state)
    };
};

const mapDispatchToProps = dispatch => ({
    autoUpdateProject: () => dispatch(autoUpdateProject()),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    onClickAccount: () => dispatch(toggleAccountMenu()),
    onRequestCloseAccount: () => dispatch(closeAccountMenu()),
    onClickFile: () => dispatch(toggleFileMenu()),
    onClickModule: () => dispatch(toggleModuleMenu()),
    onRequestCloseFile: () => dispatch(closeFileMenu()),
    onRequestCloseModule: () => dispatch(closeModuleMenu()),
    onClickEdit: () => dispatch(toggleEditMenu()),
    onRequestCloseEdit: () => dispatch(closeEditMenu()),
    onClickLogin: () => dispatch(toggleLoginMenu()),
    onRequestCloseLogin: () => dispatch(closeLoginMenu()),
    onClickMode: () => dispatch(toggleModeMenu()),
    onRequestCloseMode: () => dispatch(closeModeMenu()),
    onRequestOpenAbout: () => dispatch(openAboutMenu()),
    onRequestCloseAbout: () => dispatch(closeAboutMenu()),
    onClickSettings: () => dispatch(toggleSettingsMenu()),
    onRequestCloseSettings: () => dispatch(closeSettingsMenu()),
    onConnectServoLive: portPath => dispatch(connectServoLiveAction(portPath)),
    onDisconnectServoLive: () => dispatch(disconnectServoLiveAction()),
    onClickNew: needSave => dispatch(requestNewProject(needSave)),
    onClickRemix: () => dispatch(remixProject()),
    onClickSave: () => dispatch(manualUpdateProject()),
    onClickSaveAsCopy: () => dispatch(saveProjectAsCopy()),
    onSeeCommunity: () => dispatch(setPlayer(true)),
    onSetProjectTitle: title => dispatch(setProjectTitle(title)),
    onSetTimeTravelMode: mode => dispatch(setTimeTravel(mode))
});

export default compose(
    injectIntl,
    MenuBarHOC,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(MenuBar);
